import { type ReactNode, useCallback, useMemo } from 'react';
import { useCurrentRefinements } from 'react-instantsearch';
import { TranslationKey } from '~/lib';
import { useTranslation } from '~/shared/utils/translation';
import { Facets, STOCK_FACETS } from '~/services/instantsearch';
import { colorSwatchKey } from '~/features/productList/hooks/useColorSwatches';
import { StoreAvailabilityPanel } from '~/features/productList/components/StoreAvailability';
import { preDefinedAttributes } from './preDefinedAttributes';
import { RefinementPrice } from '../FilterList/components/RefinementPrice';
import { RefinementSlider } from '../FilterList/components/RefinementSlider';
import RefinementList from './RefinementList/RefinementList';
import RefinementAlphabeticList from './RefinementList/RefinementAlphabeticList';
import RefinementPriceList from './RefinementList/RefinementPriceList';
import RefinementListColor from './RefinementList/RefinementListColor';
import RefinementListToggle from './RefinementList/RefinementListToggles';

type FilterAttribute = {
    label: string;
    content: ReactNode;
    selectedLabel: string | null;
    hasSelection: boolean;
};

type AttributeLabel = {
    label: string;
    attribute: string | string[];
};

/**
 * Per-attribute filter metadata (label, content, selection state) shared by the
 * mobile filter list row and its detail view — `attribute` is nullable so the
 * detail view can call this unconditionally even while no filter is active.
 * `isActive` (default true) is forwarded into `content`'s VirtualizedList so a
 * facet kept mounted-but-hidden (see FilterAttributePanel) detaches from the
 * shared scroll container instead of corrupting its position tracking.
 */
export function useFilterAttribute(attribute: string | null, isActive = true): FilterAttribute {
    const { translate } = useTranslation();
    const { items: currentRefinements } = useCurrentRefinements();
    // One per pill: each ON toggle plus each selected store.
    const stockSelectionCount = currentRefinements
        .filter((item) => STOCK_FACETS.includes(item.attribute))
        .reduce((sum, item) => sum + item.refinements.length, 0);
    const attr = attribute ?? '';

    const attributeLabel = useMemo<AttributeLabel>(() => {
        if (attr === preDefinedAttributes.newsToggles) {
            return {
                label: translate('plp.facet.preDefinedNewsToggles'),
                // Not a real Algolia refinement grouping — selectedLabel/hasSelection are special-cased below instead.
                attribute: [],
            };
        }

        if (attr === preDefinedAttributes.stockAvailability) {
            return {
                label: translate('plp.facet.cust_storeIdsWithStock'),
                // Not a real refinement — selectedLabel/hasSelection are special-cased below instead.
                attribute: [],
            };
        }

        if ([Facets.SOLE_REPRESENTATION, Facets.IS_NEW].includes(attr)) {
            return {
                label: translate(`plp.facet.${attr}` as TranslationKey),
                attribute: attr,
            };
        }

        if (Facets.PRICE_RANGE === attr) {
            return {
                label: translate(`plp.facet.${attr}` as TranslationKey),
                attribute: [Facets.PRICE_RANGE, Facets.EFFECTIVE_PRICE],
            };
        }

        return {
            label: translate(`plp.facet.${attr}` as TranslationKey),
            attribute: attr,
        };
    }, [attr]);

    const content = useMemo(() => {
        switch (attr) {
            case preDefinedAttributes.stockAvailability:
                return <StoreAvailabilityPanel isActive={isActive} />;

            case preDefinedAttributes.newsToggles:
            case colorSwatchKey:
                return null;

            case Facets.SOLE_REPRESENTATION:
            case Facets.IS_NEW:
            case Facets.ALLERGY_FRIENDLY:
            case Facets.PERSONALIZATION:
                return <RefinementListToggle attributes={[{ attribute: attr, on: true }]} />;

            case Facets.PRICE_RANGE:
                return (
                    <RefinementPriceList
                        showCount
                        attribute={attr}
                        label={attributeLabel.label}
                        topContent={
                            <>
                                <RefinementSlider attribute={Facets.EFFECTIVE_PRICE} />
                                <RefinementPrice attribute={Facets.EFFECTIVE_PRICE} />
                            </>
                        }
                        isActive={isActive}
                    />
                );

            case Facets.BRAND:
            case Facets.SERIES:
            case Facets.DESIGNER:
                return (
                    <RefinementAlphabeticList
                        attribute={attr}
                        label={attributeLabel.label}
                        searchable
                        showCount
                        isActive={isActive}
                    />
                );

            case Facets.COLOR:
                return (
                    <RefinementListColor
                        attribute={attr}
                        label={attributeLabel.label}
                        searchable
                        showCount
                        isActive={isActive}
                    />
                );

            default:
                return (
                    <RefinementList
                        attribute={attr}
                        label={attributeLabel.label}
                        searchable
                        showCount
                        isActive={isActive}
                    />
                );
        }
    }, [attr, attributeLabel.label, isActive]);

    const tilbudFacets = [Facets.MEMBERS_OFFER, Facets.OFFERS];
    const priceRangeFacets = [Facets.PRICE_RANGE, Facets.EFFECTIVE_PRICE];

    const filterFacets = useCallback(
        (facets: string | string[]) =>
            currentRefinements
                .filter((item) => facets.includes(item.attribute))
                .map((item) => item.attribute),
        [currentRefinements],
    );

    const selectedFacetsTilbud = useMemo(() => filterFacets(tilbudFacets), [currentRefinements]);
    const selectedPriceRangeFacets = useMemo(
        () => filterFacets(priceRangeFacets),
        [currentRefinements],
    );

    const selectedFacets = useMemo(
        () => currentRefinements.find((item) => attributeLabel.attribute.includes(item.attribute)),
        [currentRefinements, attributeLabel],
    );

    const arraysMatch = (array1: string[], array2: string[]) =>
        array2.every((value) => array1.includes(value));

    const priceRangeFacetsCount = useMemo(
        () => currentRefinements.find((item) => item.attribute === Facets.PRICE_RANGE),
        [currentRefinements],
    );

    const selectedLabel = useMemo(() => {
        if (!selectedFacets || !attributeLabel.attribute.includes(selectedFacets.attribute)) {
            return null;
        }

        const getFacetLabel = (count: number) =>
            translate('plp.selectedFacets', { count: `${count}` });

        // Conditions for mapping the counts
        const matchConditions = [
            //if both facet of tilbud is selected
            {
                condition: selectedFacetsTilbud.length > 0,
                attributes: tilbudFacets,
                count: selectedFacetsTilbud.length,
            },
            //if price range has value in the range input and has selected facet
            {
                condition:
                    priceRangeFacetsCount &&
                    arraysMatch(selectedPriceRangeFacets, priceRangeFacets),
                attributes: priceRangeFacets,
                count: (priceRangeFacetsCount?.refinements?.length ?? 0) + 1,
            },
            //if only effective price has value
            {
                condition: selectedFacets.attribute.includes(Facets.EFFECTIVE_PRICE),
                count: 1,
            },
        ];

        // Find and return the first matching condition's facet count
        for (const { condition, attributes, count } of matchConditions) {
            if (
                condition &&
                (!attributes ||
                    attributes.some((attr2) => selectedFacets.attribute.includes(attr2)))
            ) {
                return getFacetLabel(count);
            }
        }

        // Default facet count
        return getFacetLabel(selectedFacets.refinements.length);
    }, [selectedFacets, attributeLabel, tilbudFacets, selectedFacetsTilbud]);

    if (attribute === null) {
        return { label: '', content: null, selectedLabel: null, hasSelection: false };
    }

    if (attr === preDefinedAttributes.stockAvailability) {
        return {
            label: attributeLabel.label,
            content,
            selectedLabel:
                stockSelectionCount > 0
                    ? translate('plp.selectedFacets', { count: `${stockSelectionCount}` })
                    : null,
            hasSelection: stockSelectionCount > 0,
        };
    }

    return {
        label: attributeLabel.label,
        content,
        selectedLabel,
        hasSelection: currentRefinements.some((item) =>
            attributeLabel.attribute.includes(item.attribute),
        ),
    };
}
